// =====================================================
// Role context — manages current user role
// =====================================================
//
// Phase-14.5 C1: dropped @ts-nocheck. This file is the RBAC type
// contract that the rest of the app (sidenav, shared.tsx Nav,
// ui.tsx role-switcher, CASL abilities) depends on.
//
// Phase-15 R7: extended the role catalogue from 5 to 10. The
// `student`, `instructor`, `admin`, `parent`, `org` entries stay
// shaped exactly as before. The new five — `ta`, `content_manager`,
// `support`, `moderator`, `super_admin` — get their own ROLES entry
// plus a tailored `nav` list so the sidebar surfaces only the routes
// each capability set can actually use. Display names + avatars are
// placeholders the api can later override per logged-in user.
//
// The RoleId union is the source of truth — TypeScript catches every
// switch / lookup that forgot to handle a new entry. Adjacent ability
// rules live in apps/web/src/auth/ability.ts and the api's
// `AbilityFactory`.
//
// R-NAV-1 (spec §2.0/§2.7/§22.6): the independent localStorage role is
// GONE. `RoleProvider` now DERIVES the active role from `useAuth()` —
// the real session is the single source of truth. localStorage holds at
// most a *preference* (which of a multi-role user's roles is active),
// always re-validated against the live session at render time, so a
// stale value can never grant a role the current user lacks. Anonymous
// visitors have NO active role (activeRoleId === null) → no role
// identity leaks into the shell (the "logout looks logged-in" bug).
// =====================================================
import React from "react";
import { useAuth } from "./auth/AuthContext";
import { apiRolesToLocal } from "./auth/role-map";

export type RoleId =
  | "student"
  | "instructor"
  | "admin"
  | "faculty_admin"
  | "parent"
  | "org"
  | "ta"
  | "content_manager"
  | "creator"
  | "support"
  | "moderator"
  | "super_admin"
  | "finance_manager"
  | "counselor"
  // S-NRI-3 — seven organizational / oversight roles for the ministry→holding
  // →company→unit hierarchy + the credential-issuance separation-of-duties
  // flow. The strings are BYTE-IDENTICAL to the server's seeded role names
  // (the RolesGuard matches case-sensitively).
  | "holding_manager"
  | "company_admin"
  | "company_training_manager"
  | "certification_officer"
  | "practical_assessor"
  | "ministry_supervisor"
  | "auditor";

// Permissions are deliberately a precise union (not `string`) so a
// new permission added later surfaces in every <Can> check that
// hasn't accounted for it. Phase 15 R7 keeps these coarse flags
// alongside the per-action CASL abilities — both layers are useful
// (RBAC flags for quick nav decisions, CASL for record-level checks).
export type RolePermission =
  // student
  | "learn"
  | "submit"
  | "discuss"
  | "view-grades"
  // instructor
  | "teach"
  | "grade"
  | "approve-ai"
  | "create-content"
  // admin
  | "manage-users"
  | "moderate"
  | "configure"
  | "billing"
  // faculty_admin (scope-bound to one faculty)
  | "manage-faculty"
  // parent
  | "view-child"
  | "view-bills"
  | "communicate"
  // org
  | "manage-team"
  | "view-progress"
  // ta
  | "tutor-students"
  // content_manager
  | "publish-content"
  // support
  | "audit-read"
  | "reset-passwords"
  // moderator
  | "moderate-discussions"
  // super_admin
  | "cross-tenant"
  // counselor (مشاور دانشگاه) — least-privilege wellness role: sees only its own
  // appointments, the tenant's open wellness crisis queue (duty-of-care), and a
  // SCOPED wellness summary for a student it has an appointment / open alert for.
  | "counsel";

export interface Role {
  /** Stable identifier; matches the key in ROLES. */
  id: RoleId;
  /** Persian display label ("دانشجو", "استاد", ...). */
  label: string;
  /** Display name of the seed user for this role. */
  name: string;
  /** Two-letter avatar initials. */
  avatar: string;
  /** CSS token name for the role colour ("cyan", "amber", "violet", "rose"). */
  color: string;
  /** One-line role context (department / cohort / etc.). */
  subtitle: string;
  /** Mock identifier shown under the name. */
  code: string;
  /** Route ids visible in the top nav for this role. */
  nav: string[];
  /** Route id rendered after login for this role. */
  homeRoute: string;
  /** Coarse-grained capability flags; pairs with CASL abilities. */
  permissions: RolePermission[];
}

export const ROLES: Record<RoleId, Role> = {
  student: {
    id: "student",
    label: "کارمند/فراگیر",
    name: "نسرین رضوی",
    avatar: "نر",
    color: "cyan",
    subtitle: "ارشد علوم داده",
    code: "ST-84-02-17",
    nav: ["home", "catalog", "my-courses", "progress", "tutor", "calendar", "community"],
    homeRoute: "progress",
    permissions: ["learn", "submit", "discuss", "view-grades"],
  },
  instructor: {
    id: "instructor",
    label: "مدرس",
    name: "دکتر آرش عظیمی",
    avatar: "AA",
    color: "amber",
    subtitle: "دانشکده علوم رایانه",
    code: "FA-09-A47",
    nav: ["home", "catalog", "instructor", "admin/course-content", "classroom", "calendar", "analytics", "tutor"],
    homeRoute: "instructor-dashboard",
    permissions: ["teach", "grade", "approve-ai", "create-content"],
  },
  admin: {
    id: "admin",
    label: "مدیر ارشد سامانه",
    name: "احمد محمدی",
    avatar: "AM",
    color: "violet",
    subtitle: "مدیریت کل سامانه",
    code: "AD-001",
    nav: ["home", "catalog", "progress", "admin", "analytics", "audit", "tutor", "events"],
    homeRoute: "admin",
    permissions: ["manage-users", "moderate", "configure", "billing"],
  },
  // R-NAV-2 — 11th role (spec §2.2/§2.3/§8.0). Scope-bound to one faculty.
  // `name`/`subtitle`/`code` are neutral fallbacks only — the shell shows the
  // real authed user's name and gates identity on auth (see role.tsx header).
  faculty_admin: {
    id: "faculty_admin",
    label: "مدیریت حوزهٔ آموزشی",
    name: "مدیریت حوزهٔ آموزشی",
    avatar: "رد",
    color: "cyan",
    subtitle: "مدیریت حوزهٔ آموزشی",
    code: "FAD-001",
    nav: ["faculty-dashboard", "admin/departments", "admin/programs", "admin/offerings", "admin/enrollments"],
    homeRoute: "faculty-dashboard",
    permissions: ["manage-faculty", "configure"],
  },
  parent: {
    id: "parent",
    label: "والد",
    name: "محمد رضوی",
    avatar: "MR",
    color: "rose",
    subtitle: "والد · نسرین رضوی",
    code: "PR-84-02",
    nav: ["home", "parent", "calendar", "credential", "help"],
    homeRoute: "parent",
    permissions: ["view-child", "view-bills", "communicate"],
  },
  org: {
    id: "org",
    label: "سازمان",
    name: "شرکت دانش‌بنیان فردا",
    avatar: "DF",
    color: "amber",
    subtitle: "Enterprise · ۱۴۰ کاربر",
    code: "ORG-2026-DF",
    nav: ["home", "admin", "analytics", "events", "pricing", "help"],
    // Phase-A R3: dedicated Org Manager dashboard at /org.
    homeRoute: "org",
    permissions: ["manage-team", "view-progress", "billing"],
  },
  ta: {
    id: "ta",
    label: "دستیار آموزشی",
    name: "سینا کریمی",
    avatar: "SK",
    color: "cyan",
    subtitle: "دستیار · علوم داده",
    code: "TA-2026-07",
    nav: ["home", "catalog", "my-courses", "classroom", "calendar", "progress", "tutor"],
    // Phase-A R3: dedicated TA dashboard at /ta replaces the old classroom landing.
    homeRoute: "ta",
    permissions: ["teach", "grade", "tutor-students"],
  },
  content_manager: {
    id: "content_manager",
    label: "مدیر محتوا",
    name: "ندا رحمانی",
    avatar: "NR",
    color: "amber",
    subtitle: "تیم تولید محتوا",
    code: "CM-2026-03",
    nav: ["home", "catalog", "admin/course-content", "analytics", "help"],
    // Phase-A R3: dedicated Content Manager dashboard at /content.
    homeRoute: "content",
    permissions: ["create-content", "publish-content"],
  },
  // S-NRI-ADAPT — «سازندهٔ پکیج» (استودیو دانش). A STRICT SUBSET of content_manager:
  // creator authors/prices/publishes its OWN packages + earns payout later
  // (S-NRI-6), whereas content_manager owns the WHOLE catalogue + the review
  // queue. Kept deliberately distinct so the split never needs a data migration.
  // homeRoute is the «استودیو دانش» placeholder; real studio/payout arrive with
  // S-NRI-6 / the STUDIO packages.
  creator: {
    id: "creator",
    label: "سازندهٔ پکیج",
    name: "سازندهٔ پکیج",
    avatar: "سپ",
    color: "amber",
    subtitle: "استودیو دانش · تولید و فروش پکیج",
    code: "CRE-001",
    nav: ["creator-studio", "market/studio", "market"],
    homeRoute: "creator-studio",
    permissions: ["create-content", "publish-content"],
  },
  support: {
    id: "support",
    label: "پشتیبانی",
    name: "حسین مرادی",
    avatar: "HM",
    color: "rose",
    subtitle: "پشتیبانی فنی",
    code: "SUP-2026-12",
    nav: ["home", "admin", "audit", "messages", "help"],
    // Phase-A R3: dedicated Support desk at /support.
    homeRoute: "support",
    permissions: ["audit-read", "reset-passwords"],
  },
  moderator: {
    id: "moderator",
    label: "ناظر محتوا/انجمن",
    name: "زهرا فرجی",
    avatar: "ZF",
    color: "violet",
    subtitle: "ناظر انجمن‌ها",
    code: "MOD-2026-05",
    nav: ["home", "community", "messages", "help"],
    // Phase-A R3: dedicated Moderator queue at /moderate.
    homeRoute: "moderate",
    permissions: ["moderate-discussions", "moderate"],
  },
  // Backend role «مدیر مالی» — payroll/treasury/decree access. Like
  // faculty_admin it is a staff/admin-tier role: `name`/`subtitle`/`code`
  // are neutral fallbacks; the shell shows the real authed user. Reuses the
  // existing finance-ish flags `billing` + `configure` (same pair admin uses
  // for finance) — no new permission strings invented.
  finance_manager: {
    id: "finance_manager",
    label: "مدیر مالی",
    name: "مدیر مالی",
    avatar: "مم",
    color: "amber",
    subtitle: "مدیریت مالی و حقوق",
    code: "FIN-001",
    nav: [
      "admin/finance/treasury",
      "admin/finance/payroll",
      "admin/finance/decrees",
      "admin/finance/claims",
      "admin/tuition-config",
    ],
    homeRoute: "admin/finance/treasury",
    permissions: ["billing", "configure"],
  },
  // WELL-4 — «مشاور دانشگاه». LEAST-PRIVILEGE wellness role: its workspace is the
  // counselor inbox; it sees ONLY its own appointments, the tenant's open wellness
  // crisis queue (the owner's duty-of-care decision), and a SCOPED student wellness
  // summary for a student it has an appointment / open alert for — NOT the whole
  // student record, NOT academic/finance data. `name`/`subtitle`/`code` are neutral
  // fallbacks; the shell shows the real authed user.
  counselor: {
    id: "counselor",
    label: "مشاور سلامت",
    name: "مشاور سلامت",
    avatar: "مش",
    color: "rose",
    subtitle: "مرکز مشاوره و سلامت روان",
    code: "CNS-001",
    nav: ["counselor-inbox", "counseling-appointments", "crisis-queue", "profile", "settings"],
    homeRoute: "counselor-inbox",
    permissions: ["counsel", "communicate"],
  },
  // =========================================================================
  // S-NRI-3 — seven organizational / oversight roles (ministry→holding→company
  // →unit hierarchy + credential issuance). Like faculty_admin / finance_manager
  // these are staff/admin-tier roles: `name`/`subtitle`/`code` are NEUTRAL
  // fallbacks (the shell shows the real authed user; identity gates on auth).
  // Each reuses EXISTING route ids only (S-NRI-3 ships no new pages beyond the
  // already-built /admin/organizations) and EXISTING coarse permission flags
  // (the RBAC nav decisions pair with the server CASL abilities seeded per role).
  // =========================================================================
  holding_manager: {
    id: "holding_manager",
    label: "مدیر هلدینگ",
    name: "مدیر هلدینگ",
    avatar: "مه",
    color: "violet",
    subtitle: "مدیریت هلدینگ",
    code: "HLD-001",
    nav: ["holding-dashboard", "admin/organizations", "analytics"],
    homeRoute: "holding-dashboard",
    permissions: ["manage-team", "view-progress"],
  },
  company_admin: {
    id: "company_admin",
    label: "ادمین شرکت",
    name: "ادمین شرکت",
    avatar: "اش",
    color: "cyan",
    subtitle: "مدیریت شرکت",
    code: "CMP-001",
    nav: ["company-dashboard", "admin/organizations", "analytics"],
    homeRoute: "company-dashboard",
    permissions: ["manage-team", "view-progress"],
  },
  company_training_manager: {
    id: "company_training_manager",
    label: "مدیر آموزش شرکت",
    name: "مدیر آموزش شرکت",
    avatar: "مآ",
    color: "amber",
    subtitle: "مدیریت آموزش شرکت",
    code: "CTM-001",
    nav: ["training-dashboard", "admin/organizations", "analytics"],
    homeRoute: "training-dashboard",
    permissions: ["manage-team", "view-progress"],
  },
  certification_officer: {
    id: "certification_officer",
    label: "مسئول صدور گواهی",
    name: "مسئول صدور گواهی",
    avatar: "مگ",
    color: "cyan",
    subtitle: "صدور و تأیید گواهی‌نامه",
    code: "CRT-001",
    nav: ["certification-desk", "credential"],
    homeRoute: "certification-desk",
    permissions: ["view-progress"],
  },
  practical_assessor: {
    id: "practical_assessor",
    label: "ارزیاب عملی",
    name: "ارزیاب عملی",
    avatar: "اع",
    color: "amber",
    subtitle: "ارزیابی عملی مهارت",
    code: "ASS-001",
    nav: ["assessor-dashboard", "my-courses"],
    homeRoute: "assessor-dashboard",
    permissions: ["view-progress"],
  },
  ministry_supervisor: {
    id: "ministry_supervisor",
    label: "ناظر وزارت",
    name: "ناظر وزارت",
    avatar: "نو",
    color: "violet",
    subtitle: "نظارت وزارت نیرو",
    code: "MIN-001",
    nav: ["ministry-dashboard", "admin/organizations", "analytics"],
    homeRoute: "ministry-dashboard",
    permissions: ["view-progress", "audit-read"],
  },
  auditor: {
    id: "auditor",
    label: "حسابرس",
    name: "حسابرس",
    avatar: "حس",
    color: "rose",
    subtitle: "حسابرسی و بازرسی",
    code: "AUD-001",
    nav: ["audit-dashboard", "admin/organizations", "analytics", "audit"],
    homeRoute: "audit-dashboard",
    permissions: ["audit-read", "view-progress"],
  },
  super_admin: {
    id: "super_admin",
    label: "ابرمدیر",
    name: "علی هاشمی",
    avatar: "AH",
    color: "violet",
    subtitle: "ابرمدیر · بین‌تنانتی",
    code: "SA-001",
    nav: ["home", "admin", "analytics", "audit", "tutor", "events"],
    // Phase-A R3: dedicated Super Admin overview at /super.
    homeRoute: "super",
    permissions: [
      "manage-users",
      "moderate",
      "configure",
      "billing",
      "audit-read",
      "cross-tenant",
    ],
  },
};

export interface RoleContextValue {
  /**
   * The active role's catalogue entry. NON-NULL for layout convenience
   * (colour token, Persian label, homeRoute) — 24 consumers read it
   * without a null-check. For an anonymous visitor it falls back to the
   * `student` entry as a NEUTRAL default; consumers MUST NOT treat this
   * as identity. The canonical "is anyone signed in / which role" signal
   * is `activeRoleId` (null when anonymous). Identity-displaying chrome
   * gates on `isAuthenticated`.
   */
  role: Role;
  /** Active role id, or null when anonymous. The real source of truth. */
  activeRoleId: RoleId | null;
  /** All roles the signed-in user holds, ordered by privilege ([0] = primary). */
  availableRoles: RoleId[];
  /** Mirror of `useAuth().isAuthenticated` for one-stop gating. */
  isAuthenticated: boolean;
  /**
   * Switch the active role — ONLY among `availableRoles` (a multi-role
   * user toggling their workspace, spec §3.1.6). Persisted as a
   * preference; ignored at compute time if not in `availableRoles`, so
   * it can never elevate privilege.
   */
  setRole: (id: RoleId) => void;
}

export const RoleContext = React.createContext<RoleContextValue>({
  role: ROLES.student,
  activeRoleId: null,
  availableRoles: [],
  isAuthenticated: false,
  // Default no-op — overridden by RoleProvider; only triggered if a
  // consumer renders outside the provider (which is a bug).
  setRole: () => {},
});

export const useRole = (): RoleContextValue => React.useContext(RoleContext);

// localStorage now holds at most an ACTIVE-ROLE PREFERENCE (which of a
// multi-role user's roles is active), not the role itself. Key kept as
// `digiu_role` for back-compat with the logout cleanup already in the
// navbar; semantics changed from "the role" to "the preferred role".
const STORAGE_KEY = "digiu_role";

// Set-based check is cheaper to extend than the chained || from the
// pre-R7 5-role world.
const VALID_ROLE_IDS: ReadonlySet<RoleId> = new Set<RoleId>([
  "student",
  "instructor",
  "admin",
  "faculty_admin",
  "parent",
  "org",
  "ta",
  "content_manager",
  "creator",
  "support",
  "moderator",
  "super_admin",
  "finance_manager",
  "counselor",
  // S-NRI-3
  "holding_manager",
  "company_admin",
  "company_training_manager",
  "certification_officer",
  "practical_assessor",
  "ministry_supervisor",
  "auditor",
]);

/** Read the persisted active-role PREFERENCE, or null if absent/invalid. */
const readPersistedOverride = (): RoleId | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && VALID_ROLE_IDS.has(raw as RoleId)) {
      return raw as RoleId;
    }
  } catch {
    // localStorage may be blocked (Safari private mode, iframe); fall through.
  }
  return null;
};

export interface RoleProviderProps {
  children: React.ReactNode;
}

export const RoleProvider = ({ children }: RoleProviderProps) => {
  const { user, isAuthenticated } = useAuth();

  // Roles the session actually grants, ordered by privilege. [0] is the
  // primary surface (identical to apiRoleToLocal). Empty when anonymous.
  const availableRoles = React.useMemo<RoleId[]>(
    () => apiRolesToLocal(user?.roles),
    [user?.roles],
  );

  // The active-role PREFERENCE (multi-role switcher). A preference only —
  // the effective role is always validated against availableRoles below.
  const [override, setOverride] = React.useState<RoleId | null>(readPersistedOverride);

  // Effective active role: derived purely from the session each render.
  // - anonymous            → null (no identity)
  // - preference is valid  → the preferred role
  // - else                 → primary (availableRoles[0]); authenticated
  //   users with no recognised role fall back to "student" (matches
  //   apiRoleToLocal's safe default rather than stranding them with null).
  const activeRoleId: RoleId | null = !isAuthenticated
    ? null
    : override && availableRoles.includes(override)
      ? override
      : availableRoles[0] ?? "student";

  // Reconcile the persisted preference with the live session. This is the
  // spec §2.7-1 "rebuild role from session on reload" effect: on logout
  // the preference is cleared; a stale preference from a previous user
  // (not in this session's roles) is dropped.
  React.useEffect(() => {
    if (!isAuthenticated) {
      if (override !== null) setOverride(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    } else if (override && !availableRoles.includes(override)) {
      setOverride(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }, [isAuthenticated, availableRoles, override]);

  const setRole = React.useCallback((id: RoleId) => {
    // Record the preference. activeRoleId only honours it if `id` is in
    // availableRoles at render time, so this can never grant privilege.
    if (id in ROLES) {
      setOverride(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // ignore — same reason as readPersistedOverride
      }
    }
  }, []);

  const value = React.useMemo<RoleContextValue>(
    () => ({
      role: activeRoleId ? ROLES[activeRoleId] : ROLES.student,
      activeRoleId,
      availableRoles,
      isAuthenticated,
      setRole,
    }),
    [activeRoleId, availableRoles, isAuthenticated, setRole],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};
